import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer, PolarRadiusAxis } from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Download, Lock, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import useSeo from "@/lib/useSeo";

export default function Results() {
  const { id } = useParams();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [error, setError] = useState("");
  const [revealing, setRevealing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [benchmark, setBenchmark] = useState(null);
  const [downloadingCert, setDownloadingCert] = useState(false);
  const reportRef = useRef(null);
  const certRef = useRef(null);

  useSeo({
    title: "Your Value Realisation Index Result — Langwood360",
    description: "Your board's VRI score, five-dimension spider chart, interpretation and comparison to the industry median.",
    path: `/results/${id}`,
    noindex: true,
  });

  useEffect(() => {
    api.get(`/submissions/${id}`).then(({ data }) => setSub(data)).catch(() => setError("Not found")).finally(() => setLoading(false));
    api.get(`/benchmark`).then(({ data }) => setBenchmark(data)).catch(() => {});
  }, [id]);

  const reveal = async (e) => {
    e.preventDefault();
    setRevealing(true);
    setError("");
    try {
      const { data } = await api.post(`/submissions/${id}/reveal`, { name, email, organisation: org });
      setSub(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRevealing(false);
    }
  };

  const downloadPdf = async () => {
    if (!reportRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(reportRef.current, { scale: 2, backgroundColor: "#FAF8F4" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const imgW = pageW - 48;
      const imgH = (canvas.height / canvas.width) * imgW;
      pdf.addImage(imgData, "PNG", 24, 24, imgW, imgH);
      pdf.save(`VRI-${sub.vri}-${(sub.contact?.organisation || "report").replace(/\s+/g, "-")}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  const downloadCertificate = async () => {
    if (!certRef.current) return;
    setDownloadingCert(true);
    try {
      const canvas = await html2canvas(certRef.current, { scale: 2, backgroundColor: "#F4EEDC" });
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, pageW, pageH);
      pdf.save(`Value-Realisation-Leader-${(sub.contact?.organisation || "certificate").replace(/\s+/g, "-")}.pdf`);
    } finally { setDownloadingCert(false); }
  };

  if (loading) return <div className="min-h-screen bg-linen"><Navbar /><div className="max-w-4xl mx-auto p-12 tick-label">Loading…</div></div>;
  if (!sub || error === "Not found") return <div className="min-h-screen bg-linen"><Navbar /><div className="max-w-4xl mx-auto p-12">Submission not found.</div></div>;

  const revealed = sub.revealed;

  return (
    <div className="min-h-screen bg-linen">
      <Navbar />

      <section className="max-w-6xl mx-auto px-6 lg:px-12 pt-16 pb-8">
        <div className="tick-label-accent mb-6">Your Result</div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="tick-label mb-3">Value Realisation Index</div>
            <div className="flex items-baseline gap-6">
              <div data-testid="results-vri-score" className="font-mono text-8xl md:text-9xl text-oxford leading-none">
                {sub.vri}
              </div>
              <div className="text-oxford/60 text-2xl">/ 125</div>
            </div>
            <div className="mt-6 display-serif text-3xl md:text-4xl text-orange">{sub.band}</div>
            <div className="tick-label mt-1">{sub.band_range}</div>
          </div>
          <div className="lg:col-span-4">
            <div className="border border-oxford/15 bg-linen p-6">
              <div className="tick-label mb-3">At a glance</div>
              <div className="text-sm text-charcoal">Strongest dimension</div>
              <div className="display-serif text-2xl">{sub.top_dimension}</div>
              <div className="text-sm text-charcoal mt-4">Where value is leaking</div>
              <div className="display-serif text-2xl text-orange">{sub.weakest_dimension}</div>
            </div>
          </div>
        </div>
      </section>

      {!revealed && (
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
          <div className="border border-oxford/20 bg-linen p-10 md:p-14 grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="tick-label-accent mb-4"><Lock className="inline w-3 h-3 mr-2" strokeWidth={1.5}/>Unlock the full report</div>
              <h3 className="display-serif text-3xl md:text-4xl">Your spider chart, interpretation and PDF export.</h3>
              <p className="mt-4 text-charcoal">Provide a name and email and we will unlock the full diagnostic — the five-dimension chart, the interpretation panel and a downloadable branded PDF report.</p>
              <p className="mt-4 text-charcoal text-sm italic">We do not spam. Your details are used only to send you occasional dispatches on the Framework.</p>
            </div>
            <form onSubmit={reveal} className="space-y-4">
              <div>
                <label className="tick-label block mb-2">Name</label>
                <input data-testid="reveal-name" required value={name} onChange={e=>setName(e.target.value)} className="w-full border border-oxford/20 px-3 py-3 bg-parchment focus:outline-none focus:border-oxford" />
              </div>
              <div>
                <label className="tick-label block mb-2">Email</label>
                <input data-testid="reveal-email" required type="email" value={email} onChange={e=>setEmail(e.target.value)} className="w-full border border-oxford/20 px-3 py-3 bg-parchment focus:outline-none focus:border-oxford" />
              </div>
              <div>
                <label className="tick-label block mb-2">Organisation</label>
                <input data-testid="reveal-org" required value={org} onChange={e=>setOrg(e.target.value)} className="w-full border border-oxford/20 px-3 py-3 bg-parchment focus:outline-none focus:border-oxford" />
              </div>
              {error && <div className="text-heritage text-sm" data-testid="reveal-error">{error}</div>}
              <button data-testid="reveal-submit" disabled={revealing} className="btn-orange w-full justify-center">
                {revealing ? "Unlocking…" : "Unlock full report"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5}/>
              </button>
            </form>
          </div>
        </section>
      )}

      {revealed && (
        <>
          <section className="max-w-6xl mx-auto px-6 lg:px-12 py-4">
            <div className="flex justify-end">
              <button data-testid="results-download-pdf" onClick={downloadPdf} disabled={downloading} className="btn-oxford">
                <Download className="w-3.5 h-3.5" strokeWidth={1.5}/> {downloading ? "Preparing…" : "Download PDF"}
              </button>
            </div>
          </section>
          <section ref={reportRef} className="max-w-6xl mx-auto px-6 lg:px-12 py-8 bg-linen">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-6">
                <div className="tick-label-accent mb-4">The Five Dimensions</div>
                <h3 className="display-serif text-3xl md:text-4xl">Where value is created — and where it leaks.</h3>
                <div className="mt-8 space-y-4">
                  {sub.dimensions.map(d => (
                    <div key={d.dimension} className="flex items-center gap-4">
                      <div className="w-40 tick-label">{d.dimension}</div>
                      <div className="flex-1 h-px bg-oxford/10 relative">
                        <div className="absolute top-0 left-0 h-px bg-orange" style={{ width: `${d.score}%` }} />
                      </div>
                      <div className="w-14 font-mono text-sm text-oxford text-right">{d.score}%</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-6 border border-oxford/15 bg-linen p-6" data-testid="results-spider-chart">
                <ResponsiveContainer width="100%" height={420}>
                  <RadarChart data={sub.dimensions.map(d => {
                    const median = benchmark?.ready
                      ? (benchmark.dimension_medians.find(m => m.dimension === d.dimension)?.score ?? 0)
                      : 0;
                    return { dim: d.dimension, score: d.score, median };
                  })} outerRadius="70%">
                    <PolarGrid gridType="polygon" stroke="#0E2148" strokeOpacity={0.18} />
                    <PolarAngleAxis dataKey="dim" tick={{ fill: "#0E2148", fontSize: 11 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#6D82B4", fontSize: 10 }} axisLine={false} />
                    <Radar name="You" dataKey="score" stroke="#E86A20" fill="#E86A20" fillOpacity={0.22} strokeWidth={1.8} />
                    {benchmark?.ready && (
                      <Radar name="Median" dataKey="median" stroke="#6D82B4" fill="#6D82B4" fillOpacity={0.08} strokeWidth={1.2} strokeDasharray="4 4" />
                    )}
                  </RadarChart>
                </ResponsiveContainer>
                {benchmark?.ready && (
                  <div className="mt-2 flex items-center gap-6 text-xs text-charcoal">
                    <span className="flex items-center gap-2"><span className="inline-block w-4 h-0.5 bg-orange" /> Your board</span>
                    <span className="flex items-center gap-2"><span className="inline-block w-4 h-0.5 bg-slate border-dashed" style={{borderBottom:"1px dashed #6D82B4", background:"transparent"}} /> Industry median (n={benchmark.sample_size})</span>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-16 border border-oxford/15 bg-linen p-10">
              <div className="tick-label-accent mb-3">Interpretation</div>
              <h3 className="display-serif text-4xl">{sub.interpretation.band}</h3>
              <div className="tick-label mt-1">{sub.interpretation.range}</div>
              <p className="mt-6 text-lg leading-relaxed text-charcoal max-w-3xl">{sub.interpretation.summary}</p>
              {sub.contact && (
                <div className="mt-8 pt-6 border-t border-oxford/10 text-sm text-charcoal">
                  Prepared for <span className="text-oxford font-medium">{sub.contact.name}</span> · {sub.contact.organisation}
                </div>
              )}
            </div>

            {sub.vri >= 105 && (
              <motion.div
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="mt-8 border-2 border-orange bg-linen p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between"
                data-testid="certificate-cta"
              >
                <div>
                  <div className="tick-label-accent mb-3">A Value Realisation Leader</div>
                  <h3 className="display-serif text-3xl md:text-4xl">You've earned the certificate.</h3>
                  <p className="mt-3 text-charcoal max-w-lg">A typeset, print-ready PDF you can share with your investors, board, and stakeholders.</p>
                </div>
                <button data-testid="certificate-download" onClick={downloadCertificate} disabled={downloadingCert} className="btn-orange whitespace-nowrap">
                  <Download className="w-3.5 h-3.5" strokeWidth={1.5}/> {downloadingCert ? "Preparing…" : "Download certificate"}
                </button>
              </motion.div>
            )}

            {benchmark?.ready && (
              <div className="mt-8 border border-slate/40 bg-parchment p-10" data-testid="benchmark-panel">
                <div className="tick-label-accent mb-3">Compare to the industry median</div>
                <h3 className="display-serif text-3xl md:text-4xl">
                  {sub.vri > benchmark.median_vri
                    ? `${sub.vri - benchmark.median_vri} points above the median.`
                    : sub.vri < benchmark.median_vri
                      ? `${benchmark.median_vri - sub.vri} points below the median.`
                      : "At the median."}
                </h3>
                <p className="mt-3 text-charcoal max-w-2xl">
                  Across {benchmark.sample_size} organisations measured to date, the median Value Realisation Index is <span className="font-mono text-oxford">{benchmark.median_vri}</span>.
                  {benchmark.p25_vri != null && (
                    <> The middle 50% sit between <span className="font-mono text-oxford">{benchmark.p25_vri}</span> and <span className="font-mono text-oxford">{benchmark.p75_vri}</span>.</>
                  )}
                </p>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-px bg-oxford/10">
                  {sub.dimensions.map((d) => {
                    const m = benchmark.dimension_medians.find((x) => x.dimension === d.dimension)?.score ?? 0;
                    const delta = d.score - m;
                    return (
                      <div key={d.dimension} className="bg-linen p-5">
                        <div className="tick-label">{d.dimension}</div>
                        <div className="mt-3 flex items-baseline gap-2">
                          <span className="font-mono text-2xl text-oxford">{d.score}</span>
                          <span className="text-xs text-charcoal">/ median {m}</span>
                        </div>
                        <div className={`mt-2 text-xs font-mono ${delta > 0 ? "text-orange" : delta < 0 ? "text-slate" : "text-charcoal"}`}>
                          {delta > 0 ? `+${delta}` : delta} pts
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-6 text-xs text-charcoal italic">
                  The Value Realisation Index median is updated continuously and forms the basis of the annual research publication.
                </p>
              </div>
            )}

            {benchmark && !benchmark.ready && (
              <div className="mt-8 border border-slate/40 bg-parchment p-8" data-testid="benchmark-early">
                <div className="tick-label-accent mb-2">Early cohort</div>
                <p className="text-charcoal max-w-2xl">
                  You are one of the first {benchmark.sample_size} organisations to complete the Index.
                  Industry median comparisons unlock once the cohort passes five submissions — you are helping to establish that benchmark.
                </p>
              </div>
            )}
          </section>
        </>
      )}

      {sub.board_session_code && (
        <section className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
          <div className="border border-orange/40 bg-orange/5 p-8">
            <div className="tick-label-accent">Part of a Board Session</div>
            <div className="mt-2 text-charcoal">This submission has been added to board session <span className="font-mono text-oxford">{sub.board_session_code}</span>. The facilitator will see the aggregated Board VRI and Alignment Variance once all members have responded.</div>
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
        <div className="rule mb-8" />
        <div className="flex flex-wrap gap-4">
          <Link to="/framework" className="btn-ghost">Return to the Framework</Link>
          <Link to="/board/new" className="btn-ghost">Run this for our whole board</Link>
        </div>
      </section>

      <Footer />

      {/* Hidden landscape certificate — captured by html2canvas on demand */}
      {sub.vri >= 105 && (
        <div style={{ position: "absolute", left: -99999, top: 0, width: 1123, height: 794 }}>
          <div ref={certRef} style={{
            width: 1123, height: 794, background: "#F4EEDC", color: "#0E2148",
            padding: 60, position: "relative", fontFamily: "'Cormorant Garamond', Georgia, serif",
          }}>
            {/* Border frame */}
            <div style={{ position: "absolute", inset: 20, border: "1px solid rgba(14,33,72,0.25)" }} />
            <div style={{ position: "absolute", inset: 32, border: "3px solid #E86A20" }} />
            {/* Corner glyphs */}
            <div style={{ position: "absolute", top: 44, left: 44, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "#E86A20" }}>· 01 ·</div>
            <div style={{ position: "absolute", top: 44, right: 44, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "#E86A20" }}>· 02 ·</div>
            <div style={{ position: "absolute", bottom: 44, left: 44, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "#E86A20" }}>· 03 ·</div>
            <div style={{ position: "absolute", bottom: 44, right: 44, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "#E86A20" }}>· 04 ·</div>

            {/* Content */}
            <div style={{ position: "relative", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "0 60px" }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: "0.28em", color: "#E86A20", textTransform: "uppercase" }}>Langwood360 · A Body of Work</div>
              <div style={{ height: 2, width: 60, background: "#E86A20", margin: "24px 0 36px" }} />

              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, letterSpacing: "0.22em", color: "#3B4A6B", textTransform: "uppercase" }}>Certificate of Recognition</div>
              <div style={{ fontSize: 80, lineHeight: 1, marginTop: 20, letterSpacing: "-0.02em" }}>Value Realisation</div>
              <div style={{ fontSize: 80, lineHeight: 1, marginTop: 6, letterSpacing: "-0.02em", fontStyle: "italic", color: "#E86A20" }}>Leader</div>

              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 16, color: "#3B4A6B", marginTop: 36 }}>Awarded to</div>
              <div style={{ fontSize: 46, marginTop: 8, letterSpacing: "-0.01em" }}>{sub.contact?.organisation || sub.contact?.name || "your organisation"}</div>

              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 15, color: "#3B4A6B", marginTop: 26, maxWidth: 700, lineHeight: 1.55 }}>
                For a Value Realisation Index of <span style={{ color: "#0E2148", fontFamily: "'IBM Plex Mono', monospace" }}>{sub.vri}</span> — placing this organisation within the <span style={{ color: "#0E2148" }}>{sub.interpretation?.band || "Value Realisation Leader"}</span> band. Highly aligned. Strong governance. Execution discipline. Creating options.
              </div>

              <div style={{ display: "flex", gap: 60, marginTop: 46 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: "0.22em", color: "#3B4A6B", textTransform: "uppercase" }}>VRI</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 60, color: "#0E2148", marginTop: 6 }}>{sub.vri}<span style={{ fontSize: 22, color: "#6D82B4" }}>/125</span></div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: "0.22em", color: "#3B4A6B", textTransform: "uppercase" }}>Awarded</div>
                  <div style={{ fontSize: 26, color: "#0E2148", marginTop: 12 }}>{new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: "0.22em", color: "#3B4A6B", textTransform: "uppercase" }}>By</div>
                  <div style={{ fontSize: 26, color: "#0E2148", marginTop: 12, fontStyle: "italic" }}>Steve Bird</div>
                </div>
              </div>

              <div style={{ marginTop: 36, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.22em", color: "#6D82B4", textTransform: "uppercase" }}>
                langwood360.com · The Value Realisation Framework
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
